import { argbFromHex, Hct, hexFromArgb, TonalPalette } from '@material/material-color-utilities';
import { amber, ink, lime, red, violet } from './tokens';

/**
 * Color schemes for the MUI theme.
 *
 * - Without tenant branding (platform console) we use the hand-tuned Faísca
 *   palette (violet + lime).
 * - With branding, every role is derived from the tenant's seed colors using
 *   Material 3 tonal palettes. Choosing text/background pairs by TONE (e.g.
 *   primary 40 on white, 90-on-10 containers) guarantees WCAG AA contrast no
 *   matter which color the tenant picks, so branding can never make the app
 *   unreadable.
 */

export interface RoleColors {
  main: string;
  light: string;
  dark: string;
  contrastText: string;
  container: string;
  onContainer: string;
}

export interface SchemePalette {
  primary: RoleColors;
  secondary: RoleColors;
  accent: RoleColors;
  neutral: RoleColors;
  success: RoleColors;
  info: RoleColors;
  error: RoleColors;
  warning: RoleColors;
  background: { default: string; paper: string; subtle: string };
  text: { primary: string; secondary: string; disabled: string };
  divider: string;
  action: { active: string; hover: string };
  Avatar: { defaultBg: string };
}

export interface BrandSeeds {
  primary?: string | null;
  secondary?: string | null;
}

const hex = (palette: TonalPalette, tone: number) => hexFromArgb(palette.tone(tone));

function rgba(color: string, alpha: number) {
  const value = color.replace('#', '');
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(value.slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const WHITE = '#FFFFFF';

/**
 * Tone of `palette` closest to `from` that satisfies `ok`, searching outwards.
 * Keeps the tenant's color as faithful as possible while guaranteeing contrast.
 */
function fitTone(palette: TonalPalette, from: number, ok: (color: string) => boolean): number {
  const start = Math.round(from);
  for (let delta = 0; delta <= 100; delta += 1) {
    for (const tone of [start - delta, start + delta]) {
      if (tone >= 0 && tone <= 100 && ok(hex(palette, tone))) return tone;
    }
  }
  return 40;
}

/** Light scheme role: main is the brand tone when white text on it reaches AA. */
function lightRole(p: TonalPalette, seedTone = 40): RoleColors {
  // Never lighter than tone 50 (keeps fills visible on white surfaces).
  const tone = fitTone(p, Math.min(seedTone, 50), (c) => contrastRatio(c, WHITE) >= 4.5);
  return {
    main: hex(p, tone),
    light: hex(p, Math.min(tone + 10, 60)),
    dark: hex(p, Math.max(tone - 10, 10)),
    contrastText: WHITE,
    container: hex(p, 90),
    onContainer: hex(p, 10),
  };
}

/** Dark scheme role: main must read as text on the dark background. */
function darkRole(p: TonalPalette, seedTone = 80, background = '#000000'): RoleColors {
  const tone = fitTone(
    p,
    Math.max(seedTone, 70),
    (c) => contrastRatio(c, background) >= 4.5 && contrastRatio(c, hex(p, 10)) >= 4.5,
  );
  return {
    main: hex(p, tone),
    light: hex(p, Math.min(tone + 10, 95)),
    dark: hex(p, Math.max(tone - 10, 60)),
    contrastText: hex(p, 10),
    container: hex(p, 30),
    onContainer: hex(p, 90),
  };
}

/** Faísca's original schemes: the platform's own identity. */
export const faiscaSchemes: { light: SchemePalette; dark: SchemePalette } = {
  light: {
    primary: {
      main: violet[600],
      light: violet[500],
      dark: violet[700],
      contrastText: '#FFFFFF',
      container: violet[100],
      onContainer: violet[800],
    },
    secondary: {
      main: lime[400],
      light: lime[300],
      dark: lime[500],
      contrastText: violet[900],
      container: lime[100],
      onContainer: lime[800],
    },
    accent: {
      main: lime[400],
      light: lime[300],
      dark: lime[500],
      contrastText: violet[900],
      container: lime[100],
      onContainer: lime[800],
    },
    neutral: {
      main: ink[500],
      light: ink[400],
      dark: ink[600],
      contrastText: '#FFFFFF',
      container: ink[50],
      onContainer: ink[600],
    },
    success: {
      main: lime[700],
      light: lime[600],
      dark: lime[800],
      contrastText: '#FFFFFF',
      container: lime[100],
      onContainer: lime[800],
    },
    info: {
      main: violet[500],
      light: violet[400],
      dark: violet[700],
      contrastText: '#FFFFFF',
      container: violet[100],
      onContainer: violet[700],
    },
    error: {
      main: red.light,
      light: '#E0505D',
      dark: '#A11F2C',
      contrastText: '#FFFFFF',
      container: '#FDECEE',
      onContainer: '#8C1623',
    },
    warning: {
      main: amber.light,
      light: '#C77A12',
      dark: '#7A4500',
      contrastText: '#FFFFFF',
      container: '#FFF3DF',
      onContainer: '#6B3D00',
    },
    background: { default: ink[25], paper: ink[0], subtle: ink[50] },
    text: { primary: ink[950], secondary: ink[500], disabled: ink[400] },
    divider: ink[100],
    action: { active: ink[500], hover: 'rgba(91, 45, 176, 0.06)' },
    Avatar: { defaultBg: violet[200] },
  },
  dark: {
    primary: {
      main: lime[400],
      light: lime[300],
      dark: lime[500],
      contrastText: violet[900],
      container: 'rgba(198, 239, 52, 0.14)',
      onContainer: lime[300],
    },
    secondary: {
      main: violet[300],
      light: violet[200],
      dark: violet[400],
      contrastText: ink[950],
      container: 'rgba(187, 164, 238, 0.16)',
      onContainer: violet[200],
    },
    accent: {
      main: lime[400],
      light: lime[300],
      dark: lime[500],
      contrastText: violet[900],
      container: 'rgba(198, 239, 52, 0.14)',
      onContainer: lime[300],
    },
    neutral: {
      main: ink[300],
      light: ink[200],
      dark: ink[400],
      contrastText: ink[950],
      container: 'rgba(255, 255, 255, 0.07)',
      onContainer: ink[200],
    },
    success: {
      main: lime[400],
      light: lime[300],
      dark: lime[500],
      contrastText: violet[900],
      container: 'rgba(198, 239, 52, 0.12)',
      onContainer: lime[300],
    },
    info: {
      main: violet[300],
      light: violet[200],
      dark: violet[400],
      contrastText: ink[950],
      container: 'rgba(187, 164, 238, 0.16)',
      onContainer: violet[200],
    },
    error: {
      main: red.dark,
      light: '#FFB0B3',
      dark: '#E5626A',
      contrastText: ink[950],
      container: 'rgba(255, 138, 143, 0.14)',
      onContainer: '#FFC9CB',
    },
    warning: {
      main: amber.dark,
      light: '#FFD699',
      dark: '#E0A344',
      contrastText: ink[950],
      container: 'rgba(255, 196, 107, 0.14)',
      onContainer: '#FFE1B3',
    },
    background: { default: ink[950], paper: ink[900], subtle: ink[850] },
    text: { primary: '#F4F1FB', secondary: ink[300], disabled: ink[500] },
    divider: ink[700],
    action: { active: ink[300], hover: 'rgba(198, 239, 52, 0.06)' },
    Avatar: { defaultBg: violet[700] },
  },
};

/** Material 3 "tonal spot"-like schemes from the tenant's seed colors. */
export function brandSchemes(seeds: BrandSeeds): { light: SchemePalette; dark: SchemePalette } {
  if (!seeds.primary) return faiscaSchemes;
  const seed = Hct.fromInt(argbFromHex(seeds.primary));
  const primary = TonalPalette.fromHueAndChroma(seed.hue, Math.max(seed.chroma, 36));
  const secondSeed = seeds.secondary ? Hct.fromInt(argbFromHex(seeds.secondary)) : null;
  const secondary = secondSeed
    ? TonalPalette.fromHueAndChroma(secondSeed.hue, Math.max(secondSeed.chroma, 24))
    : TonalPalette.fromHueAndChroma(seed.hue, 16);
  const accent = seeds.secondary ? secondary : TonalPalette.fromHueAndChroma(seed.hue + 60, 24);
  const neutral = TonalPalette.fromHueAndChroma(seed.hue, 6);
  const neutralVariant = TonalPalette.fromHueAndChroma(seed.hue, 10);
  const green = TonalPalette.fromHueAndChroma(145, 40);
  const blue = TonalPalette.fromHueAndChroma(250, 36);

  const secondTone = secondSeed?.tone ?? 40;
  const darkBackground = hex(neutral, 6);
  const light: SchemePalette = {
    ...faiscaSchemes.light,
    primary: lightRole(primary, seed.tone),
    secondary: lightRole(secondary, secondTone),
    accent: lightRole(accent, secondTone),
    neutral: { ...lightRole(neutralVariant), main: hex(neutralVariant, 40) },
    success: lightRole(green),
    info: lightRole(blue),
    background: { default: hex(neutral, 98), paper: hex(neutral, 100), subtle: hex(neutral, 95) },
    text: {
      primary: hex(neutral, 10),
      secondary: hex(neutralVariant, 30),
      disabled: hex(neutralVariant, 60),
    },
    divider: hex(neutralVariant, 90),
    action: { active: hex(neutralVariant, 30), hover: rgba(hex(primary, 40), 0.06) },
    Avatar: { defaultBg: hex(primary, 80) },
  };
  const dark: SchemePalette = {
    ...faiscaSchemes.dark,
    primary: darkRole(primary, seed.tone, darkBackground),
    secondary: darkRole(secondary, secondTone, darkBackground),
    accent: darkRole(accent, secondTone, darkBackground),
    neutral: { ...darkRole(neutralVariant), main: hex(neutralVariant, 80) },
    success: darkRole(green),
    info: darkRole(blue),
    background: { default: darkBackground, paper: hex(neutral, 10), subtle: hex(neutral, 17) },
    text: {
      primary: hex(neutral, 90),
      secondary: hex(neutralVariant, 80),
      disabled: hex(neutralVariant, 50),
    },
    divider: hex(neutralVariant, 30),
    action: { active: hex(neutralVariant, 80), hover: rgba(hex(primary, 80), 0.08) },
    Avatar: { defaultBg: hex(primary, 30) },
  };
  return { light, dark };
}

/** WCAG 2.x contrast ratio between two hex colors (used by tests and the brand editor). */
export function contrastRatio(a: string, b: string): number {
  const luminance = (color: string) => {
    const value = color.replace('#', '');
    const [r, g, bl] = [0, 2, 4].map((i) => {
      const c = parseInt(value.slice(i, i + 2), 16) / 255;
      return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r! + 0.7152 * g! + 0.0722 * bl!;
  };
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi! + 0.05) / (lo! + 0.05);
}
